import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { 
  Shield, 
  Users, 
  Key, 
  Plus, 
  Trash2, 
  Copy, 
  ArrowLeft,
  RefreshCw,
  UserCheck,
  UserX,
  Clock,
  CheckCircle2,
  XCircle,
  Lock,
  Loader2,
  BarChart3,
  KeyRound,
  Crown
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Email admin autorizzata
const ADMIN_EMAIL = "stefano.albera@partners.basf.com";

const AdminPage = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [adminEmail, setAdminEmail] = useState("");
  const [adminPassword, setAdminPassword] = useState("");
  
  // Data
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [keys, setKeys] = useState([]);
  const [activeTab, setActiveTab] = useState("dashboard");
  
  // Key generation
  const [daysValid, setDaysValid] = useState("7");
  const [isVipKey, setIsVipKey] = useState(false);

  const handleAdminLogin = async () => {
    if (!adminEmail.trim()) {
      toast.error("Inserisci la tua email");
      return;
    }
    
    if (!adminPassword.trim()) {
      toast.error("Inserisci la password admin");
      return;
    }
    
    // Verifica che l'email sia quella autorizzata
    if (adminEmail.toLowerCase() !== ADMIN_EMAIL.toLowerCase()) {
      toast.error("Accesso negato. Non sei autorizzato ad accedere al pannello admin.");
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/admin/login`, { 
        email: adminEmail,
        password: adminPassword 
      });
      setIsAuthenticated(true);
      toast.success("Accesso admin autorizzato!");
      loadAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Credenziali non corrette");
    } finally {
      setLoading(false);
    }
  };

  const loadAllData = async () => {
    setLoading(true);
    try {
      const [statsRes, usersRes, keysRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/admin/keys`)
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setKeys(keysRes.data);
    } catch (error) {
      toast.error("Errore caricamento dati");
    } finally {
      setLoading(false);
    }
  };

  const generateKey = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/keys/generate`, {
        days_valid: parseInt(daysValid),
        is_vip: isVipKey
      });
      const keyType = isVipKey ? "VIP (gratuita)" : "standard";
      toast.success(`Chiave ${keyType} generata: ${response.data.key}`);
      setIsVipKey(false); // Reset dopo generazione
      loadAllData();
    } catch (error) {
      toast.error("Errore generazione chiave");
    } finally {
      setLoading(false);
    }
  };

  const deleteKey = async (key) => {
    if (!window.confirm("Sei sicuro di voler eliminare questa chiave?")) return;
    
    try {
      await axios.delete(`${API}/admin/keys/${key}`);
      toast.success("Chiave eliminata");
      loadAllData();
    } catch (error) {
      toast.error("Errore eliminazione chiave");
    }
  };

  const resetUserPassword = async (email) => {
    const newPassword = window.prompt(`Inserisci la nuova password per ${email}:`);
    if (!newPassword) return;
    
    if (newPassword.length < 6) {
      toast.error("La password deve avere almeno 6 caratteri");
      return;
    }
    
    try {
      await axios.post(`${API}/admin/users/reset-password`, { 
        email, 
        new_password: newPassword 
      });
      toast.success(`Password resettata per ${email}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore reset password");
    }
  };

  const copyKey = (key) => {
    navigator.clipboard.writeText(key);
    toast.success("Chiave copiata negli appunti!");
  };

  const updateUserStatus = async (email, status) => {
    try {
      await axios.put(`${API}/admin/users/status`, { email, status });
      toast.success(`Stato utente aggiornato a ${status}`);
      loadAllData();
    } catch (error) {
      toast.error("Errore aggiornamento stato");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleDateString('it-IT', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status, isVip = false) => {
    const badges = {
      active: { bg: "bg-emerald-100", text: "text-emerald-700", label: "Attivo", icon: <CheckCircle2 className="w-3 h-3" /> },
      pending: { bg: "bg-amber-100", text: "text-amber-700", label: "In attesa", icon: <Clock className="w-3 h-3" /> },
      expired: { bg: "bg-slate-100", text: "text-slate-700", label: "Scaduto", icon: <XCircle className="w-3 h-3" /> },
      suspended: { bg: "bg-red-100", text: "text-red-700", label: "Sospeso", icon: <UserX className="w-3 h-3" /> },
      used: { bg: "bg-blue-100", text: "text-blue-700", label: "Usata", icon: <CheckCircle2 className="w-3 h-3" /> },
      vip: { bg: "bg-amber-100", text: "text-amber-700", label: "VIP", icon: <Crown className="w-3 h-3" /> },
    };
    
    // Se è VIP e attivo, mostra badge VIP
    if (isVip && status === "active") {
      return (
        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-amber-100 to-yellow-100 text-amber-700 border border-amber-200`}>
          <Crown className="w-3 h-3" />
          VIP Attivo
        </span>
      );
    }
    
    const badge = badges[status] || badges.pending;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        {badge.icon}
        {badge.label}
      </span>
    );
  };

  // Login screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-slate-900 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="text-2xl">Pannello Admin</CardTitle>
            <p className="text-slate-500 text-sm mt-2">Accesso riservato all'amministratore</p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Email</Label>
              <div className="relative">
                <Users className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  type="email"
                  placeholder="La tua email"
                  value={adminEmail}
                  onChange={(e) => setAdminEmail(e.target.value)}
                  className="pl-10"
                  data-testid="admin-email-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Password Admin</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  type="password"
                  placeholder="Password admin"
                  value={adminPassword}
                  onChange={(e) => setAdminPassword(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAdminLogin()}
                  className="pl-10"
                  data-testid="admin-password-input"
                />
              </div>
            </div>
            <Button 
              className="w-full bg-slate-900 hover:bg-slate-800"
              onClick={handleAdminLogin}
              disabled={loading}
              data-testid="admin-login-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Accedi
            </Button>
            <Button 
              variant="outline" 
              className="w-full"
              onClick={() => navigate('/')}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Torna alla Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Shield className="w-6 h-6" />
              <span className="font-bold text-lg">Pannello Admin</span>
            </div>
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-white hover:bg-slate-800"
                onClick={loadAllData}
                disabled={loading}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Aggiorna
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-white hover:bg-slate-800"
                onClick={() => navigate('/')}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Esci
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation Tabs */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex gap-6">
            {[
              { id: "dashboard", label: "Dashboard", icon: <BarChart3 className="w-4 h-4" /> },
              { id: "keys", label: "Chiavi Invito", icon: <Key className="w-4 h-4" /> },
              { id: "users", label: "Utenti", icon: <Users className="w-4 h-4" /> },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 py-4 border-b-2 transition-colors ${
                  activeTab === tab.id 
                    ? "border-blue-600 text-blue-600" 
                    : "border-transparent text-slate-600 hover:text-slate-900"
                }`}
                data-testid={`tab-${tab.id}`}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Dashboard */}
        {activeTab === "dashboard" && stats && (
          <div className="space-y-6 animate-fade-in">
            <h2 className="text-2xl font-bold text-slate-900">Dashboard</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Utenti Totali</p>
                      <p className="text-3xl font-bold text-slate-900">{stats.users.total}</p>
                    </div>
                    <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                      <Users className="w-6 h-6 text-blue-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Abbonamenti Attivi</p>
                      <p className="text-3xl font-bold text-emerald-600">{stats.users.active}</p>
                    </div>
                    <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                      <UserCheck className="w-6 h-6 text-emerald-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">In Attesa Pagamento</p>
                      <p className="text-3xl font-bold text-amber-600">{stats.users.pending}</p>
                    </div>
                    <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                      <Clock className="w-6 h-6 text-amber-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Chiavi Disponibili</p>
                      <p className="text-3xl font-bold text-slate-900">{stats.keys.available}</p>
                    </div>
                    <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center">
                      <Key className="w-6 h-6 text-slate-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Quick Actions */}
            <Card>
              <CardHeader>
                <CardTitle>Azioni Rapide</CardTitle>
              </CardHeader>
              <CardContent className="flex gap-4">
                <Button onClick={() => setActiveTab("keys")} className="bg-blue-600 hover:bg-blue-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Genera Chiave
                </Button>
                <Button variant="outline" onClick={() => setActiveTab("users")}>
                  <Users className="w-4 h-4 mr-2" />
                  Gestisci Utenti
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Keys Tab */}
        {activeTab === "keys" && (
          <div className="space-y-6 animate-fade-in">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-slate-900">Chiavi di Invito</h2>
            </div>

            {/* Generate Key */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Genera Nuova Chiave</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap items-end gap-4">
                  <div className="space-y-2">
                    <Label>Validità (giorni)</Label>
                    <Select value={daysValid} onValueChange={setDaysValid}>
                      <SelectTrigger className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">1 giorno</SelectItem>
                        <SelectItem value="3">3 giorni</SelectItem>
                        <SelectItem value="7">7 giorni</SelectItem>
                        <SelectItem value="14">14 giorni</SelectItem>
                        <SelectItem value="30">30 giorni</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-amber-50 border border-amber-200">
                    <div className="flex items-center gap-2">
                      <Crown className="w-4 h-4 text-amber-600" />
                      <Label htmlFor="vip-switch" className="text-amber-700 font-medium cursor-pointer">
                        Chiave VIP (gratuita)
                      </Label>
                    </div>
                    <Switch
                      id="vip-switch"
                      checked={isVipKey}
                      onCheckedChange={setIsVipKey}
                      data-testid="vip-switch"
                    />
                  </div>
                  
                  <Button 
                    onClick={generateKey} 
                    disabled={loading}
                    className={isVipKey ? "bg-amber-600 hover:bg-amber-700" : "bg-blue-600 hover:bg-blue-700"}
                    data-testid="generate-key-btn"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : isVipKey ? <Crown className="w-4 h-4 mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                    {isVipKey ? "Genera Chiave VIP" : "Genera Chiave"}
                  </Button>
                </div>
                
                {isVipKey && (
                  <p className="text-sm text-amber-600 mt-3">
                    La chiave VIP permette l'accesso gratuito senza pagamento Stripe.
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Keys Table */}
            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Chiave</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Stato</TableHead>
                      <TableHead>Creata</TableHead>
                      <TableHead>Scadenza</TableHead>
                      <TableHead>Usata da</TableHead>
                      <TableHead className="text-right">Azioni</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {keys.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                          Nessuna chiave trovata
                        </TableCell>
                      </TableRow>
                    ) : (
                      keys.map((key) => (
                        <TableRow key={key.key}>
                          <TableCell className="font-mono text-sm">{key.key}</TableCell>
                          <TableCell>
                            {key.is_vip ? (
                              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-amber-100 to-yellow-100 text-amber-700 border border-amber-200">
                                <Crown className="w-3 h-3" />
                                VIP
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
                                Standard
                              </span>
                            )}
                          </TableCell>
                          <TableCell>{getStatusBadge(key.status)}</TableCell>
                          <TableCell className="text-sm text-slate-500">{formatDate(key.created_at)}</TableCell>
                          <TableCell className="text-sm text-slate-500">{formatDate(key.expires_at)}</TableCell>
                          <TableCell className="text-sm">{key.used_by || "-"}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => copyKey(key.key)}
                                disabled={key.status !== "active"}
                              >
                                <Copy className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => deleteKey(key.key)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === "users" && (
          <div className="space-y-6 animate-fade-in">
            <h2 className="text-2xl font-bold text-slate-900">Gestione Utenti</h2>

            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Email</TableHead>
                      <TableHead>Azienda</TableHead>
                      <TableHead>Referente</TableHead>
                      <TableHead>Stato</TableHead>
                      <TableHead>Registrato</TableHead>
                      <TableHead className="text-right">Azioni</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                          Nessun utente trovato
                        </TableCell>
                      </TableRow>
                    ) : (
                      users.map((user) => (
                        <TableRow key={user.email}>
                          <TableCell className="font-medium">{user.email}</TableCell>
                          <TableCell>{user.azienda}</TableCell>
                          <TableCell>{user.referente}</TableCell>
                          <TableCell>{getStatusBadge(user.subscription_status, user.is_vip)}</TableCell>
                          <TableCell className="text-sm text-slate-500">{formatDate(user.created_at)}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => resetUserPassword(user.email)}
                                title="Reset Password"
                              >
                                <KeyRound className="w-4 h-4" />
                              </Button>
                              <Select 
                                value={user.subscription_status} 
                                onValueChange={(value) => updateUserStatus(user.email, value)}
                              >
                                <SelectTrigger className="w-32">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="active">Attivo</SelectItem>
                                  <SelectItem value="pending">In attesa</SelectItem>
                                  <SelectItem value="suspended">Sospeso</SelectItem>
                                  <SelectItem value="expired">Scaduto</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminPage;
